import { IconeHistorico } from '../../../components/Icones'
import { dataHora } from '../../../lib/datas'
import { ROTULO_STATUS, TERMOS } from '../../../lib/glossario'
import type { DetalheGmud } from '../../../types/gmud'

/**
 * O que a tela diz quando o servidor não oferece ação nenhuma num registro importado.
 *
 * O texto padrão de "nenhuma ação disponível" fala em estado e perfil, e manda falar com a SSTD
 * — o que está certo para uma solicitação do processo novo e é enganoso aqui: não falta
 * permissão a ninguém, o registro é fim de linha por definição. Com 1.027 deles no banco, o
 * texto errado vira 1.027 chamados possíveis para o suporte responder a mesma coisa.
 *
 * Quem decide se há ação continua sendo `/transicoes-possiveis`: isto só troca a frase mostrada
 * quando a lista que ele devolveu está vazia.
 */
export const SEM_ACOES_NO_HISTORICO = {
  titulo: 'Este registro não tem ação possível',
  ajuda:
    `Ele veio do sistema anterior e está fechado: o estado "${ROTULO_STATUS.MIGRADO_V1}" não tem saída na ` +
    'matriz de transições, então não há como enviar ao comitê, aprovar, executar ou reabrir por aqui. ' +
    'Consultar, pesquisar e imprimir o relatório, sim.',
} as const

/**
 * O aviso permanente de que esta solicitação é histórico importado, e não registro deste sistema.
 *
 * Fica no cabeçalho, sempre visível, e não numa aba: a confusão que ele evita não é de detalhe —
 * é alguém ler "Aprovado" numa tela deste sistema e entender que houve decisão de comitê. Não
 * houve, em nenhum dos 1.027 registros: nenhum passou pelo ANEXO XXI, pela conferência contra o
 * Termo de Referência ou pela comprovação de teste, porque o fluxo existia no código do sistema
 * anterior e nunca foi usado. Afirmar essa conformidade é o pior defeito que um sistema de
 * comprovação contratual pode ter.
 *
 * O `statusOriginalV1` aparece com a palavra exata de lá ("Aprovado", "Recusado"), sem tradução:
 * é o que consta nos documentos já entregues à fiscalização, e é por ele que alguém procura o
 * registro. O `aprovador` também é texto livre importado — nome digitado no sistema antigo, não
 * decisão de comitê registrada. Por isso ele só aparece aqui dentro, junto do aviso, e nunca no
 * painel de classificação, onde passaria por dado do processo novo.
 *
 * @param props a solicitação importada
 * @returns o aviso de histórico
 */
export function AvisoDeHistoricoMigrado({ gmud }: { gmud: DetalheGmud }) {
  return (
    <div className="flex gap-3 rounded-xl border border-slate-300 border-l-4 border-l-slate-500 bg-slate-100 px-4 py-3">
      <IconeHistorico className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />
      <div className="min-w-0 space-y-2 text-sm text-slate-800">
        <p className="max-w-prose">
          <strong className="font-semibold">{TERMOS.historicoMigrado.rotulo}.</strong>{' '}
          {TERMOS.historicoMigrado.ajuda}
        </p>

        <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
          {gmud.statusOriginalV1 && (
            <Procedencia rotulo="No sistema anterior constava como" valor={gmud.statusOriginalV1} />
          )}
          {gmud.aprovador && <Procedencia rotulo="Aprovador registrado lá" valor={gmud.aprovador} />}
          {gmud.migradoEm && <Procedencia rotulo="Importado em" valor={dataHora(gmud.migradoEm)} />}
        </dl>

        <p className="max-w-prose">
          <strong className="font-semibold">{SEM_ACOES_NO_HISTORICO.titulo}.</strong> {SEM_ACOES_NO_HISTORICO.ajuda}
        </p>
      </div>
    </div>
  )
}

/**
 * Um par da procedência, mantido inteiro na quebra de linha.
 *
 * `<div>` dentro do `<dl>` porque rótulo e valor não podem se separar quando a linha quebra —
 * "Aprovador registrado lá" numa linha e o nome noutra, ao lado de outro rótulo, é pior do que
 * não mostrar.
 *
 * @param props o rótulo e o valor importados
 * @returns o par
 */
function Procedencia({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex gap-1.5">
      <dt className="text-slate-600">{rotulo}</dt>
      <dd className="font-semibold text-slate-900">{valor}</dd>
    </div>
  )
}
